import { startCase } from 'lodash';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core';
import { Add, Close, Edit } from '@material-ui/icons';
import { Alert, Autocomplete } from '@material-ui/lab';
import { ConfirmDialog, WrappedTableCell } from '@sierralabs/core-ui';
import {
  EntityProperty,
  EntitySchema,
  getEntityProperties
} from '@sierralabs/entity-core';
import {
  EntitySchemaPicker,
  EntitySchemaRelationPicker,
  useEntityContext
} from '@sierralabs/entity-ui';
import {
  State,
  StateTransitionCondition,
  Workflow,
  WorkflowParams
} from '@sierralabs/workflow';
import {
  TaskContext,
  TransitionConditionConfiguratorProps,
  TransitionConditionDisplayProps,
  TransitionConditionPlugin,
  useFindTaskTypeById
} from '@sierralabs/workflow-ui';

import { useGetTaskTypes } from '../../../task-types/TaskType.hooks';
import TaskTypeSelector from '../../../task-types/components/TaskTypeSelector';
import { TaskType } from '../../../task-types/TaskType.interface';
import { useCheckDependenciesLegacy } from '../../../tasks/Task.hooks';
import { CheckDependenciesDialogView } from '../../plugins/transition-conditions/CheckDependenciesPlugin';
import {
  useFindWorkflowVersionByTaskType,
  useWorkflowStatuses
} from '../../Workflow.hooks';
import { WorkflowVersion } from '../../Workflow.interface';

export interface CheckDependenciesLegacyItem {
  name?: string;
  taskType: string; // taskType.name
  propertyName: string; // relation.propertyName
  // join relations; i.e. propertyName->relations[0]->...relations[n]
  relations: string[];
  statuses: string[]; // workflow.status
  // allow no items from this dependency
  optional?: boolean;
}

export interface CheckDependenciesLegacyParams {
  entity: { id?: string; table: string }; // entitySchema id and tableName
  dependencies?: CheckDependenciesLegacyItem[];
}

function displayStatuses(
  statuses: string[] = [],
  wrap = true,
  defaultValue = 'Any'
) {
  const allStatuses = statuses.join(', ');
  const status = wrap && statuses.length > 1 ? `(${allStatuses})` : allStatuses;
  return status || defaultValue;
}

function displayEntityProperties(properties: EntityProperty[]) {
  return properties.map((property, index) => {
    return (
      <Fragment key={index}>
        <strong>{property.relation?.displayName || '?'}</strong>
        {index < properties.length - 1 && ' -> '}
      </Fragment>
    );
  });
}

const CheckDependenciesLegacyConfigurator: React.FC<
  TransitionConditionConfiguratorProps
> = props => {
  const { taskTypeId, condition, onChange } = props;
  const [open, setOpen] = useState(false);
  const [updateIndex, setUpdateIndex] = useState(-1);
  const [removeIndex, setRemoveIndex] = useState(-1);
  if (!condition.params) {
    condition.params = {};
  }
  const params = condition.params as CheckDependenciesLegacyParams;
  const { entities } = useEntityContext();
  const { data: taskType, get } = useFindTaskTypeById(taskTypeId || 0);
  // entity schema of current Workflow
  const schema = taskType
    ? entities?.find(schema => {
        return taskType.entityId
          ? schema.id === taskType.entityId
          : schema.tableName === taskType.entityTable;
      })
    : undefined;

  const getDisplayName = (
    dependency: CheckDependenciesLegacyItem | undefined,
    includeStatus = false
  ) => {
    if (!dependency) {
      return '?';
    }
    const properties = getEntityProperties(
      [dependency.propertyName].concat(dependency.relations || []),
      schema,
      entities || []
    );
    const property = properties
      .map(property => {
        return property.relation?.displayName || property.propertyName || '?';
      })
      .join(' -> ');
    const entity = schema?.displayName || '?';
    const displayName = `${entity} -> ${property}`;
    const status = displayStatuses(dependency.statuses);
    return includeStatus ? `${displayName} -> ${status}` : displayName;
  };

  useEffect(() => {
    get();
  }, [get]);

  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Dependency</TableCell>
              <TableCell>Required State/s</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {!params.dependencies?.length && (
              <TableRow>
                <TableCell colSpan={4}>No dependencies.</TableCell>
              </TableRow>
            )}
            {params.dependencies?.map((dependency, index) => {
              const relations = [
                dependency.propertyName,
                ...(dependency.relations || [])
              ];
              const propertyName = relations[relations.length - 1];
              return (
                <TableRow key={`${dependency.name}-${propertyName}-${index}`}>
                  <WrappedTableCell>
                    {dependency.name || startCase(propertyName)}
                  </WrappedTableCell>
                  <WrappedTableCell>
                    {getDisplayName(dependency)}
                    {dependency.optional && (
                      <>
                        {' '}
                        <Typography
                          variant='body2'
                          color='textSecondary'
                          style={{ display: 'inline-block' }}
                        >
                          (optional)
                        </Typography>
                      </>
                    )}
                  </WrappedTableCell>
                  <WrappedTableCell>
                    {displayStatuses(dependency.statuses, false)}
                  </WrappedTableCell>
                  <TableCell padding='checkbox'>
                    <IconButton
                      size='small'
                      onClick={() => setUpdateIndex(index)}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      size='small'
                      onClick={() => setRemoveIndex(index)}
                    >
                      <Close />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box mt={2}>
        <Button
          variant='outlined'
          disabled={!schema}
          startIcon={<Add />}
          onClick={() => setOpen(true)}
        >
          Add Dependency
        </Button>
      </Box>

      {!schema && (
        <Box mt={2}>
          <Alert severity='error'>No schema found for this Workflow.</Alert>
        </Box>
      )}

      {schema && (
        <CheckDependenciesEntityDialog
          schema={schema}
          open={open || updateIndex > -1}
          dependency={params.dependencies?.[updateIndex]}
          title={updateIndex > -1 ? 'Update Dependency' : 'Add Dependency'}
          onCancel={() => {
            setOpen(false);
            setUpdateIndex(-1);
          }}
          onSubmit={dependency => {
            params.entity = { id: schema.id, table: schema.tableName };
            if (!params.dependencies) {
              params.dependencies = [];
            }
            if (updateIndex > -1) {
              params.dependencies.splice(updateIndex, 1, dependency);
            } else {
              params.dependencies.push(dependency);
            }
            setOpen(false);
            setUpdateIndex(-1);
            onChange?.({ ...condition });
          }}
        />
      )}

      <ConfirmDialog
        confirmLabel='Remove'
        open={removeIndex > -1}
        title='Remove Dependency'
        message={`Are you sure you want to remove ${getDisplayName(
          params.dependencies?.[removeIndex],
          true
        )} dependency?`}
        onCancel={() => setRemoveIndex(-1)}
        onConfirm={() => {
          if (!params.dependencies) {
            params.dependencies = [];
          }
          if (removeIndex > -1) {
            params.dependencies.splice(removeIndex, 1);
          }
          setRemoveIndex(-1);
          onChange?.({ ...condition });
        }}
      />
    </>
  );
};

const StyledBox = styled(Box)`
  .grid-field {
    flex: 1;
  }
  .grid-action {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 4px;
  }
`;

export interface CheckDependenciesLegacyEntityDialogProps {
  open: boolean;
  title: string;
  schema: EntitySchema;
  dependency?: CheckDependenciesLegacyItem;
  onCancel?: () => void;
  onSubmit?: (dependency: CheckDependenciesLegacyItem) => void;
}

export const CheckDependenciesEntityDialog: React.FC<
  CheckDependenciesLegacyEntityDialogProps
> = props => {
  const {
    open,
    title,
    schema,
    dependency: dependencyProp,
    onCancel,
    onSubmit
  } = props;
  const [dependency, setDependency] = useState<
    Partial<CheckDependenciesLegacyItem>
  >(dependencyProp || { relations: [], statuses: [] });
  const [workflowVersion, setWorkflowVersion] = useState<WorkflowVersion>();
  const { entities } = useEntityContext();
  const [{ data, isLoading: isFinding }, getTaskTypes] = useGetTaskTypes();
  const {
    workflowVersion: foundWorkflowVersion,
    isLoading,
    findWorkflowVersion
  } = useFindWorkflowVersionByTaskType(dependency.taskType || '');

  const workflow = useMemo(() => {
    return workflowVersion ? new Workflow(workflowVersion.config) : undefined;
  }, [workflowVersion]);
  const statuses = useWorkflowStatuses(workflow);

  const taskTypes = useMemo(() => data || [], [data]);
  const isValidStatus = useMemo(() => {
    return (
      Array.isArray(dependency.statuses) &&
      // allow optional statuses
      // dependency.statuses.length > 0 &&
      dependency.statuses.every(status => statuses.includes(status)) &&
      (!dependency.relations ||
        dependency.relations.every(relation => !!relation))
    );
  }, [dependency, statuses]);

  const properties = useMemo(() => {
    return getEntityProperties(
      [dependency.propertyName].concat(dependency.relations || []),
      schema,
      entities || []
    );
  }, [dependency, schema, entities]);

  // get last property.relation
  const relationSchema = useMemo(() => {
    return properties[properties.length - 1]?.relationSchema;
  }, [properties]);

  const entityName = relationSchema?.name;

  const entityDependency: CheckDependenciesLegacyItem | undefined =
    useMemo(() => {
      const {
        name,
        statuses,
        taskType,
        propertyName,
        optional,
        relations = []
      } = dependency;
      if (isValidStatus && statuses && taskType && propertyName) {
        return { name, statuses, taskType, propertyName, relations, optional };
      }
    }, [dependency, isValidStatus]);

  useEffect(() => {
    if (entityName) {
      getTaskTypes({ targetEntity: entityName });
    }
  }, [entityName, getTaskTypes]);

  useEffect(() => {
    dependency.taskType ? findWorkflowVersion() : setWorkflowVersion(undefined);
  }, [dependency.taskType, findWorkflowVersion]);

  useEffect(() => {
    setWorkflowVersion(foundWorkflowVersion);
  }, [foundWorkflowVersion]);

  // clear state
  useEffect(() => {
    if (!open) {
      setWorkflowVersion(undefined);
      setDependency({ relations: [], statuses: [] });
    }
  }, [open]);

  useEffect(() => {
    dependencyProp && setDependency(dependencyProp);
  }, [dependencyProp]);

  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box>
          <TextField
            fullWidth
            variant='outlined'
            label='Display Name'
            placeholder='Enter the display name of the Dependency'
            value={dependency.name || ''}
            onChange={event => {
              setDependency(dependency => {
                return { ...dependency, name: event.target.value };
              });
            }}
          />
        </Box>
        <Box mt={2}>
          <EntitySchemaPicker
            disabled
            value={schema.name}
            textFieldProps={{ label: 'Workflow Entity' }}
          />
        </Box>
        {properties.map((property, index) => (
          <StyledBox mt={2} key={index}>
            <Grid container spacing={0}>
              <Grid item className='grid-field'>
                <EntitySchemaRelationPicker
                  schema={property.schema}
                  value={property.propertyName}
                  textFieldProps={{
                    label: index === 0 ? 'Dependent Entity' : 'Entity Relation',
                    placeholder: `Select a Relationship from ${
                      property.schema?.displayName || '?'
                    }`
                  }}
                  onChange={relation => {
                    const propertyName = relation?.propertyName;
                    setDependency(dependency => {
                      dependency = { ...dependency };
                      if (!dependency.relations) {
                        dependency.relations = [];
                      }
                      if (index === 0) {
                        dependency.propertyName = propertyName;
                      } else {
                        dependency.relations[index - 1] = propertyName || '';
                      }
                      if (!propertyName) {
                        dependency.statuses = [];
                        dependency.taskType = undefined;
                        // unset all subsequent relations
                        dependency.relations.splice(index);
                      }
                      return dependency;
                    });
                  }}
                />
              </Grid>
              {index > 0 && (
                <Grid item className='grid-action'>
                  <IconButton
                    onClick={() => {
                      setDependency(dependency => {
                        dependency = {
                          ...dependency,
                          statuses: [],
                          taskType: undefined
                        };
                        dependency.relations?.splice(index - 1);
                        return dependency;
                      });
                    }}
                  >
                    <Close />
                  </IconButton>
                </Grid>
              )}
            </Grid>
          </StyledBox>
        ))}
        <Box mt={2}>
          <Button
            fullWidth
            variant='outlined'
            startIcon={<Add />}
            // disabled if no selected for last property
            disabled={!properties[properties.length - 1].propertyName}
            onClick={() => {
              setDependency(dependency => {
                dependency = {
                  ...dependency,
                  statuses: [],
                  taskType: undefined
                };
                if (!dependency.relations) {
                  dependency.relations = [];
                }
                dependency.relations.push('');
                return dependency;
              });
            }}
          >
            Add Relation
          </Button>
        </Box>
        <Box mt={2}>
          <TaskTypeSelector
            options={taskTypes}
            disabled={!relationSchema}
            isLoading={isFinding}
            value={dependency.taskType}
            textFieldProps={{
              label: 'Entity Task Type',
              placeholder: 'Select the Task Type of Entity'
            }}
            onChange={(taskType: TaskType | null) => {
              // add display name if not set
              const taskTypeLabel = taskType
                ? `${taskType.label} (${taskType.prefix})`
                : '';
              setDependency(dependency => {
                dependency = {
                  ...dependency,
                  taskType: taskType?.name,
                  name: dependency.name || taskTypeLabel
                };
                if (!dependency.taskType) {
                  dependency.statuses = [];
                }
                return dependency;
              });
            }}
          />
        </Box>
        <Box mt={2}>
          <Autocomplete
            multiple
            options={statuses}
            loading={isLoading}
            value={dependency.statuses || []}
            disabled={!dependency.taskType}
            onChange={(_, statuses) => {
              setDependency(dependency => ({ ...dependency, statuses }));
            }}
            renderInput={params => {
              const error =
                !isValidStatus &&
                Array.isArray(dependency.statuses) &&
                dependency.statuses.length > 0;
              return (
                <TextField
                  {...params}
                  fullWidth
                  variant='outlined'
                  label='Transition States'
                  placeholder={`Select the statuses for ${
                    workflow ? workflow.stateNode.name : 'the Workflow'
                  }`}
                  error={error}
                  helperText={
                    error
                      ? 'Invalid state found that is not part of the selected Workflow.'
                      : undefined
                  }
                />
              );
            }}
          />
        </Box>
        <Box mt={2}>
          <FormControlLabel
            label='Optional Dependency'
            control={
              <Checkbox
                color='primary'
                checked={dependency.optional || false}
                onChange={(_, checked) => {
                  setDependency(dependency => {
                    return { ...dependency, optional: checked };
                  });
                }}
              />
            }
          />
          <FormHelperText>
            When checked, <strong>optional dependencies</strong> will allow the
            entity to transition when there are no dependencies to check. (e.g.
            No linked documents will allow transition)
          </FormHelperText>
        </Box>

        <Collapse in={!!(relationSchema && workflow && entityDependency)}>
          <Box mt={2}>
            <Alert severity='info'>
              This condition will transition the entity (
              <strong>{schema.displayName}</strong>) when its dependencies (
              {displayEntityProperties(properties)}) are in the{' '}
              <strong>{workflow?.stateNode.name || '?'}</strong> {'->'}{' '}
              <strong>{displayStatuses(entityDependency?.statuses)}</strong>{' '}
              {(entityDependency?.statuses?.length || 0) > 1
                ? `states`
                : 'state'}
              .
            </Alert>
          </Box>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          color='primary'
          variant='contained'
          disabled={!entityDependency}
          onClick={() => {
            if (entityDependency) {
              onSubmit?.(entityDependency);
            }
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CheckDependenciesLegacyDisplay: React.FC<
  TransitionConditionDisplayProps
> = () => {
  return <>Check Dependencies (legacy)</>;
};

interface CheckDependenciesLegacyDialogProps {
  state: State<TaskContext>;
  params: CheckDependenciesLegacyParams;
  onCancel?: () => void;
  onConfirm?: () => void;
}

const CheckDependenciesLegacyDialog: React.FC<
  CheckDependenciesLegacyDialogProps
> = props => {
  const { state, params } = props;
  const { data, isLoading, error, checkDependencies } =
    useCheckDependenciesLegacy(state.context.task.id || 0);

  useEffect(() => {
    checkDependencies(params);
  }, [params, checkDependencies]);

  return (
    <CheckDependenciesDialogView
      {...props}
      error={error}
      isLoading={isLoading}
      success={!!data?.success}
      dependencies={data?.dependencies}
    />
  );
};

export const CheckDependenciesLegacyPlugin: TransitionConditionPlugin<CheckDependenciesLegacyParams> =
  {
    type: 'check-dependencies-legacy',
    label: 'Check Dependencies (legacy)',
    configurator: CheckDependenciesLegacyConfigurator,
    display: CheckDependenciesLegacyDisplay,
    validate: (condition: Partial<StateTransitionCondition>) => {
      const params = condition.params as CheckDependenciesLegacyParams;
      return !!(
        params?.entity &&
        (params.entity.id || params.entity.table) &&
        Array.isArray(params.dependencies) &&
        params.dependencies.length > 0
      );
    },
    validateImport: () => {
      return {
        severity: 'warning',
        message: 'References may differ between environments.'
      };
    },
    guard: (state: State<TaskContext>, params: WorkflowParams, render) => {
      if (state.isSimulation) return true;
      console.log('guards: check-dependencies-legacy', state, params);
      return new Promise(resolve => {
        render?.(
          <CheckDependenciesLegacyDialog
            state={state}
            params={params as CheckDependenciesLegacyParams}
            onCancel={() => {
              render?.(undefined);
              resolve(false);
            }}
            onConfirm={() => {
              render?.(undefined);
              resolve(true);
            }}
          />
        );
      });
    }
  };
